import {PointerEvent, useRef, useState} from 'react';
import {Box} from '@chakra-ui/react';
import {AnchorRect, rectFromCorners} from '../../domain/commentAnchor';

/** The regions of comments on the stage's picture, and the drawing of a new one. */
export interface StageAnchors {
    /** Waiting for a rectangle to be dragged on the picture. */
    drawing: boolean;
    /** The region marked for the comment being written. */
    draft: AnchorRect | null;
    /** A thread's region shown on the picture. */
    shown: AnchorRect | null;
    onDrawn: (rect: AnchorRect) => void;
}

interface AnchorLayerProps {
    anchors: StageAnchors;
    /** The picture's zoom: lines keep their width on screen. */
    scale: number;
}

type Point = { x: number; y: number };

const boxStyle = (rect: AnchorRect) => ({
    left: `${rect.x * 100}%`,
    top: `${rect.y * 100}%`,
    width: `${rect.w * 100}%`,
    height: `${rect.h * 100}%`,
});

/**
 * Covers the picture (inside its zoomed and panned box, so fractions of the box are fractions of the
 * picture). While drawing it takes the pointer from the stage's gestures: a drag spans the rectangle.
 */
export function AnchorLayer({anchors, scale}: AnchorLayerProps) {
    const layerRef = useRef<HTMLDivElement>(null);
    const [drag, setDrag] = useState<{ start: Point; end: Point } | null>(null);
    const line = `${2 / scale}px`;

    function toFraction(e: PointerEvent): Point {
        const rect = layerRef.current!.getBoundingClientRect();
        return {x: (e.clientX - rect.left) / rect.width, y: (e.clientY - rect.top) / rect.height};
    }

    function handlePointerDown(e: PointerEvent<HTMLDivElement>) {
        e.stopPropagation();
        if (e.button !== 0 || !e.isPrimary) return;
        e.currentTarget.setPointerCapture(e.pointerId);
        const point = toFraction(e);
        setDrag({start: point, end: point});
    }

    function handlePointerMove(e: PointerEvent<HTMLDivElement>) {
        e.stopPropagation();
        if (drag) setDrag({...drag, end: toFraction(e)});
    }

    function handlePointerUp(e: PointerEvent<HTMLDivElement>) {
        e.stopPropagation();
        if (!drag) return;
        setDrag(null);
        // A click without a drag marks nothing: still drawing.
        const rect = rectFromCorners(drag.start, toFraction(e));
        if (rect) anchors.onDrawn(rect);
    }

    const dragged = drag && rectFromCorners(drag.start, drag.end);
    const draft = anchors.drawing ? dragged : anchors.draft;
    return <Box
        ref={layerRef}
        position="absolute"
        inset={0}
        pointerEvents={anchors.drawing ? 'auto' : 'none'}
        cursor={anchors.drawing ? 'crosshair' : undefined}
        data-anchor-layer
        {...(anchors.drawing && {
            onPointerDown: handlePointerDown,
            onPointerMove: handlePointerMove,
            onPointerUp: handlePointerUp,
            onPointerCancel: () => setDrag(null),
        })}
    >
        {anchors.shown &&
            <Box
                position="absolute"
                borderColor="orange.300"
                borderRadius="sm"
                // Dims the rest of the picture (and the stage around it, which clips it).
                style={{...boxStyle(anchors.shown), borderWidth: line, boxShadow: '0 0 0 100vmax rgba(0, 0, 0, 0.55)'}}
                data-anchor-shown
            />
        }
        {draft &&
            <Box
                position="absolute"
                borderStyle="dashed"
                borderColor="blue.300"
                bg="rgba(99, 179, 237, 0.3)"
                style={{...boxStyle(draft), borderWidth: line, boxShadow: '0 0 12px rgb(0, 0, 0, 0.6)'}}
                data-anchor-draft
            />
        }
    </Box>;
}
